import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Row,
  Typography,
  Button,
  Space,
  Spin,
  Upload,
  Table,
  notification,
} from "antd";
import queryString from "query-string";
import { UploadOutlined } from "@ant-design/icons";
import { forEach, isEmpty } from "lodash";
import parseOrders from "../helpers/parseOrders";
import { teamsFetch } from "../redux/actions/teamActions";
import { homebasesFetch } from "../redux/actions/homebaseActions";
import { createOrUpdateOrders } from "../redux/actions/orderActions";
const { Title } = Typography;

const ProcessCsvOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [fileList, setFileList] = useState([]);
  const teams = useSelector((state) => state?.team?.all);
  const homebases = useSelector((state) => state?.homebase?.all);
  const isLoading = useSelector((state) => state?.loader?.isLoading);
  const [isProcessed, setIsProcessed] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [failedCreateIds, setFailedCreateIds] = useState([]);
  const [failedUpdateIds, setFailedUpdateIds] = useState([]);
  const { authToken, clientId } = queryString.parse(location.search);

  const handleError = useCallback(
    (error = {}) => {
      const { message = "", data = {} } = error;
      navigate(`/error-fallback${location.search}`, {
        state: { from: location, message, data },
      });
    },
    [navigate, location]
  );

  useEffect(() => {
    const authParams = { clientId, authToken };
    dispatch(teamsFetch(authParams, navigate, handleError));
    dispatch(homebasesFetch(authParams, navigate, handleError));
  }, [dispatch, clientId, authToken, navigate, handleError]);

  const homebaseIds = useMemo(() => {
    const ids = [];
    if (isEmpty(homebases)) return ids;
    forEach(homebases, (homebase) => {
      ids.push(homebase?.id);
    });
    return ids;
  }, [homebases]);

  const teamIds = useMemo(() => {
    const ids = [];
    if (isEmpty(teams)) return ids;
    forEach(teams, (team) => {
      if (team.status === "ACTIVE") {
        ids.push(team?.teamId);
      }
    });
    return ids;
  }, [teams]);

  const opts = {
    multiple: false,
    accept: "text/csv",
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      return setFileList(newFileList);
    },
    beforeUpload: (file) => {
      setFileList([file]);
      setIsProcessed(false);
      setDataSource([]);
      return false;
    },
    fileList,
  };

  const onSubmit = async () => {
    if (isEmpty(fileList)) return;

    // Count CSV lines and enforce a 10,000 line limit
    try {
      const file = fileList[0];
      const text = await file.text();
      const lineCount = text
        .split(/\r\n|\r|\n/)
        .filter((line) => line.length > 0).length;

      if (lineCount > 10000) {
        notification.error({
          message: "CSV too large",
          description:
            "The selected CSV has more than 10,000 lines. Please upload a smaller file.",
        });
        return;
      }
    } catch (e) {
      notification.error({
        message: "Unable to read file",
        description: "We couldn't read the CSV to validate its size.",
      });
      return;
    }

    const orderParams = {
      clientId,
      homebaseIds,
      teamIds,
    };

    parseOrders(fileList[0], orderParams)
      .then(({ data }) => {
        const authParams = { clientId, authToken };
        dispatch(
          createOrUpdateOrders(data, authParams, navigate, handleError)
        ).then((res) => {
          const combinedOrders = [];
          const maxLength = Math.max(
            res?.createdOrders?.orderIds?.length,
            res?.updatedOrders?.orderIds?.length,
            res?.createdOrders?.failedIds?.length,
            res?.updatedOrders?.failedIds?.length
          );

          for (let i = 0; i < maxLength; i++) {
            combinedOrders.push({
              createdOrder: res?.createdOrders?.orderIds[i] || "",
              updatedOrder: res?.updatedOrders?.orderIds[i] || "",
              errorCreation: res?.createdOrders?.failedIds[i] || "",
              errorUpdation: res?.updatedOrders?.failedIds[i] || "",
            });
          }

          if (combinedOrders.length) {
            setDataSource(combinedOrders);
          }
          setFailedCreateIds(res?.createdOrders?.failedIds || []);
          setFailedUpdateIds(res?.updatedOrders?.failedIds || []);
          setIsProcessed(true);
        });
      })
      .catch(handleError)
      .finally(() => setFileList([]));
  };

  const downloadFailed = (ids, type) => {
    const header = "orderId";
    const content = [header, ...ids].join("\n");
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${type}-failed-order-ids.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      title: "Created Orders",
      dataIndex: "createdOrder",
      key: "createdOrder",
    },
    {
      title: "Updated Orders",
      dataIndex: "updatedOrder",
      key: "updatedOrder",
    },
    {
      title: "Create Failed Ids",
      dataIndex: "errorCreation",
      key: "errorCreation",
    },
    {
      title: "Update Failed Ids",
      dataIndex: "errorUpdation",
      key: "errorUpdation",
    },
  ];

  return (
    <React.Fragment>
      <div style={{ padding: "50px 0px" }}>
        <Row justify="center">
          <Space size="large" direction="vertical" align="center">
            <Title level={5}>Create / Update Orders</Title>
            <Upload className="uploadBtnContainer" {...opts}>
              <Button style={{ width: 350 }} icon={<UploadOutlined />}>
                Select file
              </Button>
            </Upload>
            <Button
              style={{ width: 350 }}
              type="primary"
              disabled={isEmpty(fileList)}
              onClick={onSubmit}
            >
              Submit
            </Button>
            {isLoading && <Spin tip="Processing..." />}
            {isProcessed && (
              <>
                <br />
                <Title level={5}>File Process Results</Title>
                {(failedCreateIds.length > 0 || failedUpdateIds.length > 0) && (
                  <Space size="middle">
                    {failedCreateIds.length > 0 && (
                      <Button
                        onClick={() =>
                          downloadFailed(failedCreateIds, "create")
                        }
                      >
                        Download Create Failed ({failedCreateIds.length})
                      </Button>
                    )}
                    {failedUpdateIds.length > 0 && (
                      <Button
                        onClick={() =>
                          downloadFailed(failedUpdateIds, "update")
                        }
                      >
                        Download Update Failed ({failedUpdateIds.length})
                      </Button>
                    )}
                  </Space>
                )}
                {dataSource.length ? (
                  <Table
                    size="large"
                    columns={columns}
                    dataSource={dataSource}
                    bordered
                  />
                ) : (
                  <p> Oops! No Orders Found to create / update</p>
                )}
              </>
            )}
          </Space>
        </Row>
      </div>
    </React.Fragment>
  );
};

export default ProcessCsvOrders;
